import type { UnreadHuddleComment } from '../notifications/queries';
import type { Task } from '../tasks/types';

/** One task's unread portal comments, newest notification first. */
export interface TaskHuddleUnread {
  count: number;
  newestCommentId: string;
  newestAt: string;
}

export interface HuddleUnreadSummary {
  total: number;
  taskNames: string[];
  latestAt: string | null;
  newestTaskId: string | null;
  newestCommentId: string | null;
  byTask: Record<string, TaskHuddleUnread>;
}

const EMPTY: HuddleUnreadSummary = {
  total: 0,
  taskNames: [],
  latestAt: null,
  newestTaskId: null,
  newestCommentId: null,
  byTask: {},
};

/**
 * The notification query is user-scoped by RLS. This final lens is the other
 * half of the rule: only tasks that still belong to this My Day section count.
 */
export function huddleUnreadForSection(
  rows: readonly UnreadHuddleComment[],
  tasks: readonly Task[],
  sectionId: string,
): HuddleUnreadSummary {
  const tasksHere = new Map(
    tasks
      .filter((task) => task.my_day_section_id === sectionId)
      .map((task) => [task.id, task] as const),
  );
  const relevant = rows
    .filter((row) => tasksHere.has(row.task_id))
    .sort((a, b) => {
      if (a.created_at !== b.created_at) return a.created_at < b.created_at ? 1 : -1;
      return a.id < b.id ? 1 : -1;
    });

  const newest = relevant[0];
  if (newest === undefined) return EMPTY;

  const byTask: Record<string, TaskHuddleUnread> = {};
  const taskNames: string[] = [];
  for (const row of relevant) {
    const existing = byTask[row.task_id];
    if (existing) {
      existing.count += 1;
      continue;
    }
    const task = tasksHere.get(row.task_id);
    if (!task) continue;
    byTask[row.task_id] = {
      count: 1,
      newestCommentId: row.comment_id,
      newestAt: row.created_at,
    };
    taskNames.push(task.title);
  }

  return {
    total: relevant.length,
    taskNames,
    latestAt: newest.created_at,
    newestTaskId: newest.task_id,
    newestCommentId: newest.comment_id,
    byTask,
  };
}

/**
 * The names a My Day section can carry and still be the huddle section by
 * name: the old one and the new one (TAP Console, KYU_DECISION 28 Sept).
 * Trimmed and case-insensitive, as the two checks it replaces were.
 */
export function isHuddleSectionName(name: string): boolean {
  const clean = name.trim().toLowerCase();
  return clean === 'alex huddle' || clean === 'tap console';
}

export function huddleCommentLabel(count: number): string {
  return `Alex left ${String(count)} new ${count === 1 ? 'comment' : 'comments'}`;
}

export function huddleLatestTime(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
